import { motion } from "framer-motion";
import { ShieldCheck, AlertCircle, FileCheck } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const PROVIDERS = ["Sun Life", "Manulife", "Canada Life", "GreenShield", "Desjardins", "Blue Cross"];

export const Insurance = () => (
    <section id="insurance" data-testid="insurance-section" className="border-b border-line bg-sand">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
            <p
                className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-mutedink"
                data-testid="insurance-label"
            >
                Insurance &amp; billing
            </p>
            <h2
                className="max-w-3xl font-syne text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl"
                data-testid="insurance-title"
            >
                Straight answers about
                <span className="font-cormorant italic font-medium text-ochre"> paying for care.</span>
            </h2>
            <div className="mt-14 grid grid-cols-1 border border-line lg:grid-cols-3">
                <motion.div
                    data-testid="insurance-direct-billing"
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="border-b border-line bg-bone p-8 sm:p-10 lg:border-b-0 lg:border-r"
                >
                    <ShieldCheck size={22} className="text-ochre" />
                    <h3 className="mt-5 font-syne text-xl font-medium tracking-tight sm:text-2xl">
                        We direct-bill most plans
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-mutedink">
                        Bring your extended-health benefits card and we bill your insurer directly —
                        no paying up front and chasing reimbursement.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-2" data-testid="insurance-provider-tags">
                        {PROVIDERS.map((p) => (
                            <span
                                key={p}
                                className="border border-line bg-badge px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-mutedink"
                            >
                                {p}
                            </span>
                        ))}
                    </div>
                </motion.div>
                <motion.div
                    data-testid="insurance-ohip-notice"
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, delay: 0.08, ease: EASE }}
                    className="border-b border-line bg-ink p-8 text-bone sm:p-10 lg:border-b-0 lg:border-r lg:border-line"
                >
                    <AlertCircle size={22} className="text-ochre" />
                    <h3 className="mt-5 font-syne text-xl font-medium tracking-tight sm:text-2xl">
                        A note on OHIP
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-bone/70">
                        OHIP does not cover private physiotherapy. Most patients use
                        extended-health benefits through work, school, or a spouse's plan — or pay
                        directly.
                    </p>
                </motion.div>
                <motion.div
                    data-testid="insurance-no-referral"
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, delay: 0.16, ease: EASE }}
                    className="bg-bone p-8 sm:p-10"
                >
                    <FileCheck size={22} className="text-ochre" />
                    <h3 className="mt-5 font-syne text-xl font-medium tracking-tight sm:text-2xl">
                        No referral required
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-mutedink">
                        A doctor's referral is not required to book. Some insurance plans ask for
                        one before reimbursing — check your policy, then come straight to us.
                    </p>
                </motion.div>
            </div>
        </div>
    </section>
);
